import { CommonModule } from '@angular/common';
import { ChangeDetectionStrategy, Component, effect, inject, OnInit } from '@angular/core';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { MatPaginatorModule, PageEvent } from '@angular/material/paginator';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { MatSortModule, Sort } from '@angular/material/sort';
import { MatTableDataSource, MatTableModule } from '@angular/material/table';
import { RouterLink } from '@angular/router';

import { Store } from '@ngrx/store';

import { RoleAccessDirective } from '../../../@core/directives/role-access.directive';
import { FiltersAndPaginationPayloadService } from '../../../@core/services/filters-and-pagination-payload.service';
import { OrdersActions } from '../../../@store/orders/orders.actions';
import { singleOrderDetails } from '../../../@store/orders/orders.model';
import {
  selectMyOrdersTotalPrice,
  selectOrdersList,
  selectOrdersListError,
  selectOrdersListLength,
  selectOrdersListLoading,
} from '../../../@store/orders/orders.selectors';
import { OrderTableData } from '../../../@store/orders/orders.state.interface';
import { QuotesActions } from '../../../@store/quotes/quotes.actions';
import { selectIsOrdersManager } from '../../../@store/user/user.selectors';
import { CONFIG } from '../../../config';
import { SafeAny } from '../../../types/safe-any';
import { PaginationComponent } from '../../@shared/pagination/pagination.component';

@Component({
  selector: 'ciwc-my-orders',
  standalone: true,
  imports: [
    CommonModule,
    MatTableModule,
    MatSortModule,
    MatPaginatorModule,
    MatProgressSpinnerModule,
    PaginationComponent,
    RoleAccessDirective,
    MatIconModule,
    MatButtonModule,
    RouterLink,
  ],
  templateUrl: './my-orders.component.html',
  styleUrls: ['./my-orders.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class MyOrdersComponent implements OnInit {
  private readonly store = inject(Store);
  private readonly filtersAndPaginationPayloadService = inject(FiltersAndPaginationPayloadService);
  readonly myOrdersList = this.store.selectSignal(selectOrdersList);
  readonly myOrdersListLength = this.store.selectSignal(selectOrdersListLength);
  readonly myOrdersTotalPrice = this.store.selectSignal(selectMyOrdersTotalPrice);
  readonly ordersListLoading = this.store.selectSignal(selectOrdersListLoading);
  readonly ordersListError = this.store.selectSignal(selectOrdersListError);
  readonly isOrdersManager = this.store.selectSignal(selectIsOrdersManager);
  readonly CONFIG = CONFIG;

  private myQuotesFilters: SafeAny;

  pageSizeOptions = CONFIG.PAGE_SIZE_OPTIONS;
  pageSize = CONFIG.PAGE_SIZE;
  totalMyOrders = 0;
  pageIndex = 0;
  displayedColumns: string[] = [
    'orderId',
    'description',
    'clientPurchaseOrder',
    'caNumber',
    'promisedDate',
    'dateCreated',
    'status',
    'client',
    'assignee',
    'totalQuantity',
    'orderTotalPrice',
    'dateUpdated',
    'actions',
  ];
  currentSort: Sort = { active: 'dateCreated', direction: 'desc' };
  dataSource: MatTableDataSource<OrderTableData> = new MatTableDataSource<OrderTableData>();
  errorText = CONFIG.ERROR_MESSAGES.DEFAULT;

  constructor() {
    effect(() => {
      this.initValues();
    });
  }

  ngOnInit(): void {
    this.fetchData();
    this.hideQuantityAndPriceForManager();
    this.initFilterValues();
  }

  sortData(sort: Sort): void {
    this.currentSort = sort;
    const formattedSort = sort.direction === 'desc' ? `-${sort.active}` : sort.active;

    this.filtersAndPaginationPayloadService.updateSortFromMaterial(this.currentSort);

    this.store.dispatch(
      OrdersActions.loadMyOrders({
        payload: {
          offset: this.pageIndex + 1,
          limit: this.pageSize,
          sort: formattedSort,
          assignee: localStorage.getItem('id'),
          status: this.myQuotesFilters?.status || null,
          archived: false,
        },
      }),
    );
  }

  handlePageEvent(event: PageEvent): void {
    this.pageSize = event.pageSize;
    this.pageIndex = event.pageIndex;

    const formattedSort =
      this.currentSort.direction === 'desc'
        ? `-${this.currentSort.active}`
        : this.currentSort.active;

    this.store.dispatch(
      OrdersActions.loadMyOrders({
        payload: {
          offset: this.pageIndex + 1,
          limit: this.pageSize,
          sort: formattedSort,
          assignee: localStorage.getItem('id'),
          status: this.myQuotesFilters?.status || null,
          archived: false,
        },
      }),
    );
  }

  private fetchData(): void {
    this.store.dispatch(QuotesActions.loadStatuses({ payload: { identifier: 'ORDERS_STATUS' } }));
    this.store.dispatch(
      OrdersActions.loadMyOrders({
        payload: {
          offset: 1,
          limit: 10,
          sort: '-dateCreated',
          assignee: localStorage.getItem('id'),
          archived: false,
        },
      }),
    );

    this.filtersAndPaginationPayloadService.updateSortFromMaterial(this.currentSort);
  }

  private initValues(): void {
    const orderTableData = this.myOrdersList()?.map((order: singleOrderDetails) => ({
      orderId: order.orderId,
      description: order.description || '',
      clientPurchaseOrder: order.clientPurchaseOrder || '',
      caNumber: order.caNumber || '',
      dueDate: order.dueDate || '',
      dateCreated: order.dateCreated,
      status: order.status,
      client: `${order.client.firstName} ${order.client.lastName}, ${order.client.company}, ${order.client.code}`,
      assignee: order.assignee ? `${order.assignee.firstName} ${order.assignee.lastName}` : 'N/A',
      quantity: order.totalQuantity?.toString() || '',
      totalPrice: order.orderTotalPrice?.toString() || '',
      lastEdited: order.dateUpdated || order.dateCreated,
      id: order.id,
    }));

    this.dataSource.data = orderTableData as OrderTableData[];

    this.totalMyOrders = this.myOrdersListLength() || 0;
  }

  private hideQuantityAndPriceForManager(): void {
    if (this.isOrdersManager()) {
      this.displayedColumns = this.displayedColumns.filter(
        (column) => column !== 'totalQuantity' && column !== 'orderTotalPrice',
      );
    }
  }

  private initFilterValues(): void {
    this.filtersAndPaginationPayloadService.myQuotesFilterPayload$.subscribe((payload) => {
      this.myQuotesFilters = payload;
      this.pageIndex = 0;
      this.pageSize = 10;
    });
  }
}
